/**
 * WebKit starts an anchor download asynchronously and drops it if the object URL is already revoked
 * when it gets there, so the URL is kept alive well past the click (the delay FileSaver.js settled on).
 */
const OBJECT_URL_REVOKE_DELAY_MS = 40_000;
const SHARE_SHEET_DISMISSED_ERROR_NAME = 'AbortError';

/**
 * Desktop (and mobile Firefox) navigates straight to the URL and lets Content-Disposition name the file.
 * Other mobile browsers fetch the file as a blob first so the saved file gets a readable name: iOS offers
 * the share sheet while a user gesture is still live, everything else gets a plain blob download.
 */
export const downloadFileFromUrl = async (downloadUrl: string, baseName: string, extension: string): Promise<void> => {
  if (!shouldDownloadAsBlob()) {
    downloadViaNavigation(downloadUrl);
    return;
  }

  const blob = await fetchBlob(downloadUrl);
  if (blob === null) {
    downloadViaNavigation(downloadUrl);
    return;
  }

  await downloadBlobOnMobile(blob, `${baseName}_${Date.now()}.${extension}`);
};

const shouldDownloadAsBlob = (): boolean => {
  return window.PMW.isMobileOS() && !window.PMW.isFirefox();
};

const fetchBlob = async (url: string): Promise<Blob | null> => {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      return null;
    }
    return await response.blob();
  } catch {
    return null;
  }
};

const downloadBlobOnMobile = async (blob: Blob, filename: string): Promise<void> => {
  const file = new File([blob], filename, {type: blob.type});
  if (!canShareFileNow(file)) {
    downloadBlobViaAnchor(blob, filename);
    return;
  }

  try {
    await navigator.share({files: [file]});
  } catch (error) {
    if (isShareSheetDismissal(error)) {
      return;
    }
    downloadBlobViaAnchor(blob, filename);
  }
};

/**
 * The Web Share API needs a live user gesture. A download that finishes through polling has none, and
 * share() then rejects with NotAllowedError, so the sheet is only offered while the gesture is active.
 * Browsers without the userActivation API still get the attempt and fall back on rejection.
 */
const canShareFileNow = (file: File): boolean => {
  if (!window.PMW.isIOS() || typeof navigator.canShare !== 'function' || !navigator.canShare({files: [file]})) {
    return false;
  }
  return navigator.userActivation === undefined || navigator.userActivation.isActive;
};

const isShareSheetDismissal = (error: unknown): boolean => {
  return typeof error === 'object' && error !== null && 'name' in error && error.name === SHARE_SHEET_DISMISSED_ERROR_NAME;
};

const downloadBlobViaAnchor = (blob: Blob, filename: string): void => {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.setTimeout((): void => {
    URL.revokeObjectURL(objectUrl);
  }, OBJECT_URL_REVOKE_DELAY_MS);
};

const downloadViaNavigation = (url: string): void => {
  window.location.href = url;
};
